import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type RemoveDeliveryNoteMappingResponse = {
  code: number;
  id: UUID;
  requestId: UUID | undefined;
};

type RemoveDeliveryNoteMappingPayload = {
  accRefs: UUID[];
  replaceAfterSigning?: boolean;
  replaceCcFields?: boolean;
  replacePrFields?: boolean;
  sites: UUID[];
};

const EMPTY_DATA: RemoveDeliveryNoteMappingPayload = {
  accRefs: [],
  replaceAfterSigning: false,
  replaceCcFields: true,
  replacePrFields: true,
  sites: [], // Keep sites and cost centers for already signed dlns.
};

type RemoveDeliveryNoteMappingMutationOptions = UseMutationOptions<
  RemoveDeliveryNoteMappingResponse,
  Error,
  UUID
>;

/**
 * Removes the mapping of a delivery note.
 *
 * @param deliveryNoteId - The UUID of the delivery note whose mapping should be removed.
 * @returns A promise that resolves with the API response containing the removal status.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/update_direct_dln_mapping_asset__id__direct_mapping_put
 */
export const removeDeliveryNoteMapping = async (deliveryNoteId: UUID) => {
  const response = await vestigasApi
    .put(ENDPOINT.DELIVERY_NOTE.REMOVE_MAPPING(deliveryNoteId), {
      json: EMPTY_DATA,
    })
    .json<RemoveDeliveryNoteMappingResponse>();

  return response;
};

/**
 * Custom hook for removing a delivery note mapping.
 *
 * @param options - Optional mutation configuration.
 * @returns An object containing the mutation function and additional properties.
 */
export const useMutationRemoveDeliveryNoteMapping = (
  options?: RemoveDeliveryNoteMappingMutationOptions,
) => {
  return useMutation({
    mutationFn: async (deliveryNoteId: UUID) =>
      removeDeliveryNoteMapping(deliveryNoteId),
    ...options,
  });
};
